import { ChangeEvent } from "react";
import SubmitSkeleton from "../SubmitSkeleton";
import { useCaseContext } from "../../../context/CaseContext";
import CustomLabel from "../../common/CustomLabel";
import Select from "react-select";
import { vehicleBrandOptions, vehicleColorOptions } from "../resources";
import { AbuserVehicleInfo } from "../../../types";

const AbuserVehicleInfoStage = ({
  setStep,
}: {
  setStep: (step: number) => void;
}) => {
  const { abuserDetails, hasAbuserInfo, updateCaseData } = useCaseContext();

  const handleAbuserDetailChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    updateCaseData("abuserDetails", {
      ...abuserDetails,
      hasAbuserVehicleInfo: value === "yes",
    });
  };

  const handleAbuserVehicleInfoChange = (
    value: string | undefined,
    field: keyof AbuserVehicleInfo
  ) => {
    const otherFields = ["brand", "carColor"];

    if (otherFields.includes(field) && value === "other") {
      const otherVal = { custom: true, customValue: "" };

      updateCaseData("abuserDetails", {
        ...abuserDetails,
        vehicleInfo: {
          ...abuserDetails.vehicleInfo,
          [field]: otherVal,
        },
      });
    } else {
      updateCaseData("abuserDetails", {
        ...abuserDetails,
        vehicleInfo: {
          ...abuserDetails.vehicleInfo,
          [field]: value,
        },
      });
    }
  };

  const handleOtherChange = (
    field: "brand" | "carColor",
    e: ChangeEvent<HTMLInputElement>
  ) => {
    const { value } = e.target;

    updateCaseData("abuserDetails", {
      ...abuserDetails,
      vehicleInfo: {
        ...abuserDetails.vehicleInfo,
        [field]: {
          custom: true,
          customValue: value,
        },
      },
    });
  };

  const handleNextStep = () => {
    setStep(10);
  };

  return (
    <SubmitSkeleton
      onPrev={() => (hasAbuserInfo ? setStep(8) : setStep(7))}
      onNext={handleNextStep}
      progress={9 / 10}
    >
      <div className="flex flex-col gap-4">
        <CustomLabel
          labelText="Could you provide any information about the abuser's vehicle?"
          isRequired
        >
          <div className="flex flex-col">
            <label className="flex items-center gap-3 pr-6 pl-3 cursor-pointer">
              <input
                type="radio"
                name="hasVehicleInfo"
                value="yes"
                checked={abuserDetails?.hasAbuserVehicleInfo}
                onChange={handleAbuserDetailChange}
                className="form-radio"
              />
              <span className="font-medium text-base leading-4 text-black py-[14px]">
                Yes
              </span>
            </label>

            <label className="flex items-center gap-3 pr-6 pl-3 cursor-pointer">
              <input
                type="radio"
                name="hasVehicleInfo"
                value="no"
                checked={!abuserDetails?.hasAbuserVehicleInfo}
                onChange={handleAbuserDetailChange}
                className="form-radio"
              />
              <span className="font-medium text-base leading-4 text-black py-[14px]">
                No
              </span>
            </label>
          </div>
        </CustomLabel>

        {abuserDetails?.hasAbuserVehicleInfo && (
          <>
            <CustomLabel labelText="Abuser's Vehicle Brand (Optional)">
              <Select
                components={{
                  IndicatorSeparator: () => null,
                }}
                // menuClassName="mt-3 w-[54%] border-none shadow-selectMenu"
                className="font-medium text-[14px] leading-4 cursor-pointer"
                placeholder="Select abuser's vehicle brand"
                defaultInputValue={
                  typeof abuserDetails.vehicleInfo?.brand === "object"
                    ? "object"
                    : abuserDetails?.vehicleInfo?.brand || ""
                }
                onChange={(selectedOption) =>
                  handleAbuserVehicleInfoChange(selectedOption?.value, "brand")
                }
                options={vehicleBrandOptions}
              />
            </CustomLabel>

            {abuserDetails?.vehicleInfo &&
              typeof abuserDetails.vehicleInfo?.brand === "object" &&
              abuserDetails.vehicleInfo?.brand.custom && (
                <label className="flex flex-col gap-y-2">
                  <span className="font-bold text-[14px] leading-[16.94px] text-black mt-2">
                    Other
                  </span>
                  <input
                    type="text"
                    value={abuserDetails.vehicleInfo?.brand.customValue}
                    onChange={(e) => handleOtherChange("brand", e)}
                    placeholder="Please specify..."
                    className="border border-gray-3 py-2 px-3 rounded-md box-border outline-none focus:w-[99%] focus:ring-1 focus:ring-black focus:border-transparent "
                  />
                </label>
              )}

            <CustomLabel labelText="Abuser's Vehicle Model (Optional)">
              <input
                type="text"
                name="model"
                placeholder="Enter abuser's car's model"
                value={abuserDetails?.vehicleInfo?.model || ""}
                onChange={(e) =>
                  handleAbuserVehicleInfoChange(e.target.value, "model")
                }
                className="font-medium text-[14px] leading-4 text-gray-7 w-full border border-gray-3 rounded-md py-2 px-3 outline-none focus:ring-1 focus:ring-black focus:border-transparent cursor-pointer"
              />
            </CustomLabel>

            <CustomLabel labelText="Abuser's Vehicle Color (Optional)">
              <Select
                components={{
                  IndicatorSeparator: () => null,
                }}
                // menuClassName="mt-3 w-[54%] border-none shadow-selectMenu"
                className="font-medium text-[14px] leading-4 cursor-pointer"
                placeholder="Select abuser's vehicle color"
                defaultInputValue={
                  typeof abuserDetails.vehicleInfo?.carColor === "object"
                    ? "object"
                    : abuserDetails?.vehicleInfo?.carColor || ""
                }
                onChange={(selectedOption) =>
                  handleAbuserVehicleInfoChange(
                    selectedOption?.value,
                    "carColor"
                  )
                }
                options={vehicleColorOptions}
              />
            </CustomLabel>

            {abuserDetails?.vehicleInfo &&
              typeof abuserDetails.vehicleInfo?.carColor === "object" &&
              abuserDetails.vehicleInfo?.carColor.custom && (
                <label className="flex flex-col gap-y-2">
                  <span className="font-bold text-[14px] leading-[16.94px] text-black mt-2">
                    Other
                  </span>
                  <input
                    type="text"
                    value={abuserDetails.vehicleInfo?.carColor.customValue}
                    onChange={(e) => handleOtherChange("carColor", e)}
                    placeholder="Please specify..."
                    className="border border-gray-3 py-2 px-3 rounded-md box-border outline-none focus:w-[99%] focus:ring-1 focus:ring-black focus:border-transparent "
                  />
                </label>
              )}

            <CustomLabel labelText="License Plate (Optional)">
              <input
                type="text"
                name="licensePlate"
                placeholder="Enter abuser's car's license plate"
                value={abuserDetails?.vehicleInfo?.licensePlate || ""}
                onChange={(e) =>
                  handleAbuserVehicleInfoChange(e.target.value, "licensePlate")
                }
                className="font-medium text-[14px] leading-4 text-gray-7 w-full border border-gray-3 rounded-md py-2 px-3 outline-none focus:ring-1 focus:ring-black focus:border-transparent cursor-pointer"
              />
            </CustomLabel>
          </>
        )}
      </div>
    </SubmitSkeleton>
  );
};

export default AbuserVehicleInfoStage;
